/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {error} from '@ion/core/src/logger/logger';
import {
  geofenceEventsBulkAction,
  loadGeofenceEvents,
  loadGeofenceEventsCount
} from '@ion/core/src/services/ArcGIS';
import {LoadGeofenceEventsParams} from '@ion/core/src/types/services';
import {FilterModel} from '@ion/dls/src/grid/IonGridUtils';
import {PageInitializeAction} from '@/types/redux';
import {getAccountId} from '../helpers';

// name of reducer
export const NAME                                = 'geofenceEvents';

export const INITIALIZE_GEOFENCE_EVENTS          = 'INITIALIZE_GEOFENCE_EVENTS';

export const SET_GEOFENCE_EVENTS_GRID_COLUMNS    = 'SET_GEOFENCE_EVENTS_GRID_COLUMNS';

export const UPDATE_GEOFENCE_EVENTS_GRID_COLUMNS = 'UPDATE_GEOFENCE_EVENTS_GRID_COLUMNS';

export const GET_GEOFENCE_EVENTS_REQUEST         = 'GET_GEOFENCE_EVENTS_REQUEST';
export const GET_GEOFENCE_EVENTS_RESPONSE        = 'GET_GEOFENCE_EVENTS_RESPONSE';
export const GET_GEOFENCE_EVENTS_ERROR           = 'GET_GEOFENCE_EVENTS_ERROR';

export const GEOFENCE_EVENTS_GRID_REFRESHED      = 'GEOFENCE_EVENTS_GRID_REFRESHED';
export const SET_GEOFENCE_GRID_FILTER_MODEL      = 'SET_GEOFENCE_GRID_FILTER_MODEL';

export const REFRESH_GEOFENCE_EVENTS_DATA        = 'REFRESH_GEOFENCE_EVENTS_DATA';

export const GET_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST  = 'GET_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST';
export const GET_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE = 'GET_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE';
export const GET_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR    = 'GET_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR';

export const BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST  = 'BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST';
export const BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE = 'BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE';
export const BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR    = 'BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR';

/**
 * initialize GeoFence Events Page
 */
export const initializeGeofenceEvents = (): PageInitializeAction => {
  return {type: INITIALIZE_GEOFENCE_EVENTS};
};

/**
 * To refresh GeofenceEvents Grid.
 */
export const geofenceEventsGridRefreshed = (): PageInitializeAction => {
  return {type: GEOFENCE_EVENTS_GRID_REFRESHED};
};

export const getRefreshedGeofenceEvents = (val: boolean = false) => {
  return {type: REFRESH_GEOFENCE_EVENTS_DATA, val};
};

// Invoked by IonGrid when the grid filter model changes.
export const setFilterModel = (model: FilterModel) => ({
  type : SET_GEOFENCE_GRID_FILTER_MODEL, model
});

/**
 * Get Geofence Events
 */
export const getGeofenceEvents = ({
  sort,
  filter,
  callback: callbackFn,
  includeSubGroups = true,
  pageId = 1,
  pageSize = 20
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_GEOFENCE_EVENTS_REQUEST});
    const data: LoadGeofenceEventsParams = {
      accountId : getAccountId(getState),
      sortOn    : sort?.[0],
      includeSubGroups,
      pageId,
      pageSize
    };
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const [totalCount, items] = await Promise.all([
        loadGeofenceEventsCount(data),
        loadGeofenceEvents(data)
      ]);
      dispatch({type: GET_GEOFENCE_EVENTS_RESPONSE, count: totalCount});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_GEOFENCE_EVENTS_ERROR, error: err});
      return err;
    }
  };
};

/**
 * To remove bulk events acknowledge error.
 */
export const removeBulkEventsAcknowledgeError = (error) => ({
  type : BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR, error : error
});

/**
 * To remove single event acknowledge error.
 */
export const removeEventAcknowledgeError = (error) => ({
  type : GET_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR, error : error
});

/**
 * Invoked when the multiple or single GeofenceEvents are acknowledged.
 */
export const acknowledgeGeofenceEvents = (selectedEvents): Function => {
  const events = selectedEvents.map(({OBJECTID, messageType}) => ({
    alertStatus : 'Acknowledged',
    OBJECTID,
    messageType
  }));
  return async(dispatch: Function): Promise<any> => {
    dispatch({type: BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_REQUEST});
    try {
      const bulkActionResponse = await geofenceEventsBulkAction(events);
      dispatch({type: BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_RESPONSE, events: bulkActionResponse});
    } catch (err) {
      dispatch({type: BULK_GEOFENCE_EVENTS_ACKNOWLEDGE_ERROR, error: err});
      error(err);
    }
  };
};
